import { Input } from 'almanac-ui/TextInputField/Input'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Edit3, LinkAngled, Trash2 } from 'blaze-ui/icons'
import Styles from './WebsiteListItem.module.scss'
import { useState, useRef } from 'react'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { startCase } from 'lodash'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { truncate } from 'utils'
import useOutsideClick from 'hooks/useOutsideClick'

const MAX_TITLE_LENGTH = 35
const MAX_URL_LENGTH = 50

interface WebsiteListItemProps {
  url: string
  favicon?: string
  onRemove: () => void
  onUpdate: (url: string) => void
}

export const WebsiteListItem = ({ url: initialUrl, favicon, onRemove, onUpdate }: WebsiteListItemProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [url, setUrl] = useState(initialUrl)
  const editingRef = useRef<HTMLDivElement>(null)

  const { isValid, errorMessage } = useIsUrlCrawlable(url.trim())

  const handleCancel = () => {
    setUrl(initialUrl)
    setIsEditing(false)
  }

  useOutsideClick(editingRef, () => {
    if (isEditing) {
      handleCancel()
    }
  })

  const handleSave = async () => {
    if (!isValid || !url.trim()) {
      const displayMessage = typeof errorMessage === 'string' ? errorMessage : 'Please enter a valid URL'
      showErrorMessageToast(displayMessage)
      return
    }

    try {
      await onUpdate(url.trim())
      setIsEditing(false)
    } catch (error) {
      showErrorMessageToast('Failed to update website. Please try again.')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div ref={editingRef} className={Styles.website}>
        <LinkAngled size={24} />

        <Input
          id={`website-${initialUrl}`}
          name={`website-${initialUrl}`}
          type="url"
          value={url}
          placeholder="Enter other URL from your business website..."
          size="sm"
          autoFocus
          autoCorrect="off"
          onChange={(e) => {
            setUrl(e.target.value)
          }}
          onKeyDown={handleKeyDown}
        />
        <div className={Styles.buttonGroup}>
          <Button variant="ghost-tertiary" size="sm" onPress={handleSave} aria-label="save" isDisabled={!url.trim()}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  const websiteUrl = initialUrl.replace('https://', '').replace('http://', '').replace('www.', '')
  const urlParts = websiteUrl.split('/').slice(1)
  const urlName = urlParts.length > 0 ? urlParts.map((part) => startCase(part)).join(' | ') : ''
  const domain = websiteUrl.substring(0, websiteUrl.lastIndexOf('.'))

  return (
    <div className={Styles.website}>
      <div className={Styles.nameContainer}>
        {favicon ? <img src={favicon} className={Styles.favicon} alt="favicon" /> : <LinkAngled size={24} />}
        <Text variant="smallList" lineClamp={1}>
          {truncate(urlName || domain, MAX_TITLE_LENGTH)}
        </Text>
        <Text variant="secondary" color="var(--dark-40)" lineClamp={1}>
          {truncate(initialUrl, MAX_URL_LENGTH)}
        </Text>
      </div>
      <div className={Styles.actions}>
        <IconButton variant="editor" icon={Edit3} size="sm" onPress={() => setIsEditing(true)} aria-label="edit" />
        <IconButton variant="editor" icon={Trash2} size="sm" onPress={onRemove} aria-label="delete" />
      </div>
    </div>
  )
}
